import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CONTENT, STATE, STATUS } from "./consts";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { addSchema, AddZoneFormValues } from "./validations";
import { toast } from "@/components/Toast";
import { useAddStateLawMutation, useGetStateLawById, useGetStates, useUpdateStateLawMutation } from "@/services";
import { capitalize, getBaseRoute, getRequiredFields } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

const useAddEdit = () => {
  const {id, action} = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const basePath = getBaseRoute(useLocation().pathname);
  const isView = action == "view";
  const isEdit = action == "edit";
  const [stateKey, setStateKey] = useState<number>(Math.random() * 2);

  const form = useForm<AddZoneFormValues>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      [STATE]: "",
      [CONTENT]: "",
    },
  });

  const requiredFields = getRequiredFields(addSchema);

  const breadcrumbData = [
    {
      link: `/${basePath}/${routes.masterData}/${routes.stateLaw}`,
      name: `State Laws`,
    },
    {
      link: "",
      name: id ? `${capitalize(action as any)} State Law`: "Add State Law",
    },
  ];

  const onBackClick = () => {
    navigate(`/${basePath}/${routes.masterData}/${routes.stateLaw}`);
  };

  const {data: stateList, isLoading: isStateLoading} = useGetStates({excludeCreatedStates: id ? false : true});

  const {data: stateLawData, isLoading: isStateLawDataLoading} = useGetStateLawById({id: id as string, queryParams: {
    enabled: !!id
  }});

  useEffect(() => {
    if (stateList?.data) {
      setStateKey(Math.random() * 2);
    }
  }, [stateList]);

  useEffect(() => {
    if (stateLawData?.data) {
      form.reset({
        [STATE]: stateLawData?.data?.[STATE]?.toString() || "",
        [CONTENT]: stateLawData?.data?.[CONTENT]?.toString() || "",
      });
      setStateKey(Math.random() * 2);
    }
  }, [stateLawData]);

  const { mutate: addInfo, isPending: isAddPending } = useAddStateLawMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      toast.success("Law Added Successfully");
      queryClient.invalidateQueries({
        queryKey: ["state-law"],
      });
      onBackClick();
    },
  });

  const { mutate: updateInfo, isPending: isUpdatePending } = useUpdateStateLawMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      toast.success("Law Updated Successfully");
      queryClient.invalidateQueries({
        queryKey: ["state-law"],
      });
      onBackClick();
    },
  });

  const onSubmit = (data: AddZoneFormValues, status: string) => {
    if (id) {
      updateInfo({ stateLawId: id, data: {...data, [STATUS]: status} });
    } else {
      addInfo({ data: {...data, [STATUS]: status} });
    }
  };

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    onCancel: onBackClick,
    stateList,
    isStateLoading,
    isStateLawDataLoading,
    isView,
    isUpdatePending,
    stateKey,
    isEdit
  };
};

export default useAddEdit;
